package com.shadowings.gero.data.repository

import androidx.datastore.core.DataStore
import androidx.datastore.preferences.core.MutablePreferences
import androidx.datastore.preferences.core.Preferences
import androidx.datastore.preferences.core.edit
import com.shadowings.gero.data.datasource.DataStoreKeys
import com.shadowings.gero.data.datasource.chapterEight
import com.shadowings.gero.data.datasource.chapterEleven
import com.shadowings.gero.data.datasource.chapterFive
import com.shadowings.gero.data.datasource.chapterFour
import com.shadowings.gero.data.datasource.chapterNine
import com.shadowings.gero.data.datasource.chapterOne
import com.shadowings.gero.data.datasource.chapterSeven
import com.shadowings.gero.data.datasource.chapterSix
import com.shadowings.gero.data.datasource.chapterTen
import com.shadowings.gero.data.datasource.chapterFifteen
import com.shadowings.gero.data.datasource.chapterSixteen
import com.shadowings.gero.data.datasource.chapterFourteen
import com.shadowings.gero.data.datasource.chapterThirteen
import com.shadowings.gero.data.datasource.chapterThree
import com.shadowings.gero.data.datasource.chapterTwelve
import com.shadowings.gero.data.datasource.chapterTwo
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.SavedBuilderWord
import com.shadowings.gero.domain.model.ThemeMode
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.presentation.widget.notifyStrikeWidgetDataChanged
import com.shadowings.gero.util.currentTimeMillis
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.serialization.json.Json
import kotlin.math.max

class UserDataRepositoryImpl(
    private val dataStore: DataStore<Preferences>
) : UserDataRepository {

    private val repositoryScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    // null until DataStore has actually been read: a UserData() placeholder here made one-shot
    // userData.first() reads at startup (streak backfill, reminder scheduling, the cloud bootstrap
    // merge) act on an empty profile, and the bootstrap then wiped local-only progress.
    private val loadedUserData: StateFlow<UserData?> = dataStore.data
        .map { it.toUserData() }
        .stateIn(
            scope = repositoryScope,
            started = SharingStarted.Eagerly,
            initialValue = null
        )

    override val userData: Flow<UserData> = loadedUserData.filterNotNull()

    override val currentUserDataOrNull: UserData? get() = loadedUserData.value

    private fun Preferences.toUserData(): UserData = let { prefs ->
        val stars = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_STARS) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_STARS) to value
            else null
        }.toMap()

        val perfect = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_PERFECT) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_PERFECT)
            else null
        }.toSet()

        val explanations = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_EXPLANATIONS))
                name.removePrefix(DataStoreKeys.PREFIX_EXPLANATIONS)
            else null
        }.toSet()

        val alynColors = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_ALYN_COLOR) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_ALYN_COLOR) to value
            else null
        }.toMap()

        val niloColors = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_NILO_COLOR) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_NILO_COLOR) to value
            else null
        }.toMap()

        val zetaColors = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_ZETA_COLOR) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_ZETA_COLOR) to value
            else null
        }.toMap()

        val maskoColors = prefs.asMap().mapNotNull { (key, value) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_MASKO_COLOR) && value is Int)
                name.removePrefix(DataStoreKeys.PREFIX_MASKO_COLOR) to value
            else null
        }.toMap()

        val favoriteHieros = prefs.asMap().mapNotNull { (key, _) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_FAVORITE_HIERO))
                name.removePrefix(DataStoreKeys.PREFIX_FAVORITE_HIERO)
            else null
        }.toSet()

        val favoriteWords = prefs.asMap().mapNotNull { (key, _) ->
            val name = key.name
            if (name.startsWith(DataStoreKeys.PREFIX_FAVORITE_WORD))
                name.removePrefix(DataStoreKeys.PREFIX_FAVORITE_WORD)
            else null
        }.toSet()

        val rawDatesString = prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] ?: ""
        val lastQuizDateInt = prefs[DataStoreKeys.LAST_QUIZ_DATE] ?: 0
        val legacyStrike = prefs[DataStoreKeys.STRIKE] ?: 0

        val completedQuizDates = if (rawDatesString.isNotBlank()) {
            rawDatesString.split(",").filter { it.isNotBlank() }.distinct().sorted()
        } else if (lastQuizDateInt > 0 && legacyStrike > 0) {
            val lastQuizEpoch = com.shadowings.gero.util.dateIntToEpochDays(lastQuizDateInt)
            (0 until legacyStrike).map { i ->
                com.shadowings.gero.util.epochDaysToDateString(lastQuizEpoch - i)
            }.reversed()
        } else {
            emptyList()
        }

        val computedStrike = if (completedQuizDates.isNotEmpty()) {
            com.shadowings.gero.util.calculateStreak(completedQuizDates)
        } else {
            legacyStrike
        }

        val savedBuilderWords = runCatching {
            val raw = prefs[DataStoreKeys.SAVED_BUILDER_WORDS]
            if (!raw.isNullOrBlank()) {
                Json.decodeFromString<List<SavedBuilderWord>>(raw)
            } else {
                emptyList()
            }
        }.getOrDefault(emptyList())

        val streakRestorationTokens = decodeStreakRestorationTokens(prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS])

        val wrongAnswers = decodeWrongAnswers(prefs[DataStoreKeys.WRONG_ANSWERS])

        UserData(
            stars = stars,
            perfect = perfect,
            explanations = explanations,
            experience = prefs[DataStoreKeys.EXPERIENCE_TOTAL] ?: prefs[androidx.datastore.preferences.core.intPreferencesKey("coins_total")] ?: 0,
            strike = computedStrike,
            tutorialDone = prefs.asMap()
                .any { (key, _) -> key.name == DataStoreKeys.TUTORIAL_DONE.name },
            alynColors = alynColors,
            niloColors = niloColors,
            zetaColors = zetaColors,
            maskoColors = maskoColors,
            hearts = prefs[DataStoreKeys.HEARTS] ?: GameConstants.DEFAULT_HEARTS,
            lastOpenDate = prefs[DataStoreKeys.LAST_OPEN_DATE] ?: 0,
            lastQuizDate = if (lastQuizDateInt > 0) lastQuizDateInt else {
                if (completedQuizDates.isNotEmpty()) com.shadowings.gero.util.dateStringToDateInt(completedQuizDates.last()) else 0
            },
            favoriteHieros = favoriteHieros,
            favoriteWords = favoriteWords,
            language = prefs[DataStoreKeys.APP_LANGUAGE] ?: "",
            walkthroughDone = prefs[DataStoreKeys.WALKTHROUGH_DONE] ?: false,
            compendioWalkthroughDone = prefs[DataStoreKeys.COMPENDIO_WALKTHROUGH_DONE] ?: false,
            builderWalkthroughDone = prefs[DataStoreKeys.BUILDER_WALKTHROUGH_DONE] ?: false,
            charactersWalkthroughDone = prefs[DataStoreKeys.CHARACTERS_WALKTHROUGH_DONE] ?: false,
            profileWalkthroughDone = prefs[DataStoreKeys.PROFILE_WALKTHROUGH_DONE] ?: false,
            completedQuizDates = completedQuizDates,
            consumablesUpdatedAt = (prefs[DataStoreKeys.CONSUMABLES_UPDATED_AT] ?: 0L).toString(),
            lastModified = (prefs[DataStoreKeys.LAST_MODIFIED] ?: 0L).toString(),
            lastSeenChangelogVersion = prefs[DataStoreKeys.LAST_SEEN_CHANGELOG_VERSION] ?: "",
            savedBuilderWords = savedBuilderWords,
            reviewPromptDone = prefs[DataStoreKeys.REVIEW_PROMPT_DONE] ?: false,
            isFullVersionUnlocked = prefs[DataStoreKeys.IS_FULL_VERSION_UNLOCKED] ?: false,
            streakRestorationTokens = streakRestorationTokens,
            streakRestorationBackfillDone = prefs[DataStoreKeys.STREAK_RESTORATION_BACKFILL_DONE] ?: false,
            streakRestorationBadgeSeen = prefs[DataStoreKeys.STREAK_RESTORATION_BADGE_SEEN] ?: false,
            answeredFeaturePolls = decodeAnsweredFeaturePolls(prefs[DataStoreKeys.ANSWERED_FEATURE_POLLS]),
            wrongAnswers = wrongAnswers,
            wrongAnswersUpdatedAt = (prefs[DataStoreKeys.WRONG_ANSWERS_UPDATED_AT] ?: 0L).toString(),
            favoriteHierosUpdatedAt = (prefs[DataStoreKeys.FAVORITE_HIEROS_UPDATED_AT] ?: 0L).toString(),
            favoriteWordsUpdatedAt = (prefs[DataStoreKeys.FAVORITE_WORDS_UPDATED_AT] ?: 0L).toString(),
        )
    }

    override val testMode: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.TEST_MODE] ?: false
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = false
    )

    override val showCheats: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.SHOW_CHEATS] ?: false
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = false
    )

    override val themeMode: Flow<ThemeMode> = dataStore.data.map { prefs ->
        ThemeMode.fromStorage(prefs[DataStoreKeys.THEME_MODE])
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = ThemeMode.System
    )

    override val soundEnabled: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.SOUND_ENABLED] ?: true
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = true
    )

    override val hapticEnabled: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.HAPTIC_ENABLED] ?: true
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = true
    )

    override val hideOptions: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.HIDE_OPTIONS] ?: false
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = false
    )

    override val reminderEnabled: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.REMINDER_ENABLED] ?: true
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = true
    )

    override val reminderHour: Flow<Int> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.REMINDER_HOUR] ?: 21
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = 21
    )

    override val reminderMinute: Flow<Int> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.REMINDER_MINUTE] ?: 0
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = 0
    )

    override val calendarPromptDone: Flow<Boolean> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.CALENDAR_PROMPT_DONE] ?: false
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = false
    )

    override val calendarPromptDismissedAt: Flow<Long> = dataStore.data.map { prefs ->
        prefs[DataStoreKeys.CALENDAR_PROMPT_DISMISSED_AT] ?: 0L
    }.stateIn(
        scope = repositoryScope,
        started = SharingStarted.Eagerly,
        initialValue = 0L
    )

    private fun decodeStreakRestorationTokens(raw: String?): Map<String, Boolean> = runCatching {
        if (!raw.isNullOrBlank()) {
            Json.decodeFromString<Map<String, Boolean>>(raw)
        } else {
            emptyMap()
        }
    }.getOrDefault(emptyMap())

    private fun decodeAnsweredFeaturePolls(raw: String?): Map<String, Boolean> = runCatching {
        if (!raw.isNullOrBlank()) {
            Json.decodeFromString<Map<String, Boolean>>(raw)
        } else {
            emptyMap()
        }
    }.getOrDefault(emptyMap())

    private suspend fun editUserData(block: (MutablePreferences) -> Unit) {
        dataStore.edit { prefs ->
            block(prefs)
            prefs[DataStoreKeys.LAST_MODIFIED] = currentTimeMillis()
        }
    }

    override suspend fun setLanguage(language: String) {
        editUserData { prefs ->
            prefs[DataStoreKeys.APP_LANGUAGE] = language
        }
    }

    override suspend fun setThemeMode(themeMode: ThemeMode) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.THEME_MODE] = themeMode.storageValue
        }
    }

    override suspend fun setSoundEnabled(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.SOUND_ENABLED] = enabled
        }
    }

    override suspend fun setHapticEnabled(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.HAPTIC_ENABLED] = enabled
        }
    }

    override suspend fun setHideOptions(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.HIDE_OPTIONS] = enabled
        }
    }

    override suspend fun setStar(id: String, stars: Int) {
        val starsKey = DataStoreKeys.starsKey(id)
        val perfectKey = DataStoreKeys.perfectKey(id)
        editUserData { prefs ->
            val previousStars = prefs[starsKey] ?: 0
            val newStars = max(previousStars, stars)
            prefs[starsKey] = newStars
            // Regola "perfect": due volte consecutive a 3 stelle
            if (stars == 3 && previousStars == 3) {
                prefs[perfectKey] = 1
            }
        }
    }

    override suspend fun setLessonCompleted(lessonId: String) {
        editUserData { prefs ->
            prefs[DataStoreKeys.explanationsKey(lessonId)] = 1
        }
    }

    override suspend fun setTutorialCompleted() {
        val todayDate = com.shadowings.gero.util.currentDateAsInt()
        val currentVersion = com.shadowings.gero.domain.model.currentChangelogEntry.version
        editUserData { prefs ->
            prefs[DataStoreKeys.TUTORIAL_DONE] = 1
            if (prefs[DataStoreKeys.LAST_OPEN_DATE] == null) {
                prefs[DataStoreKeys.LAST_OPEN_DATE] = todayDate
            }
            if (prefs[DataStoreKeys.LAST_SEEN_CHANGELOG_VERSION] == null) {
                prefs[DataStoreKeys.LAST_SEEN_CHANGELOG_VERSION] = currentVersion
            }
        }
    }

    override suspend fun setWalkthroughCompleted() {
        editUserData { prefs ->
            prefs[DataStoreKeys.WALKTHROUGH_DONE] = true
        }
    }

    override suspend fun setLastSeenChangelogVersion(version: String) {
        editUserData { prefs ->
            prefs[DataStoreKeys.LAST_SEEN_CHANGELOG_VERSION] = version
        }
    }

    override suspend fun setTabWalkthroughCompleted(tabIndex: Int) {
        editUserData { prefs ->
            val key = when (tabIndex) {
                1 -> DataStoreKeys.COMPENDIO_WALKTHROUGH_DONE
                2 -> DataStoreKeys.BUILDER_WALKTHROUGH_DONE
                3 -> DataStoreKeys.CHARACTERS_WALKTHROUGH_DONE
                4 -> DataStoreKeys.PROFILE_WALKTHROUGH_DONE
                else -> null
            }
            if (key != null) {
                prefs[key] = true
            }
        }
    }

    override suspend fun setTestMode(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.TEST_MODE] = enabled
        }
    }

    override suspend fun setShowCheats(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.SHOW_CHEATS] = enabled
        }
    }

    override suspend fun setAlynColor(categoryKey: String, index: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.alynColorKey(categoryKey)] = index
        }
    }

    override suspend fun setNiloColor(categoryKey: String, index: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.niloColorKey(categoryKey)] = index
        }
    }

    override suspend fun setZetaColor(categoryKey: String, index: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.zetaColorKey(categoryKey)] = index
        }
    }

    override suspend fun setMaskoColor(categoryKey: String, index: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.maskoColorKey(categoryKey)] = index
        }
    }

    override suspend fun addStars(amount: Int) {
        editUserData { prefs ->
            val current = prefs[DataStoreKeys.STARS_BONUS] ?: 0
            prefs[DataStoreKeys.STARS_BONUS] = current + amount
        }
    }

    override suspend fun addExperience(amount: Int) {
        editUserData { prefs ->
            val current = prefs[DataStoreKeys.EXPERIENCE_TOTAL] ?: prefs[androidx.datastore.preferences.core.intPreferencesKey("coins_total")] ?: 0
            prefs[DataStoreKeys.EXPERIENCE_TOTAL] = current + amount
            prefs.remove(androidx.datastore.preferences.core.intPreferencesKey("coins_total"))
            prefs[DataStoreKeys.CONSUMABLES_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun setHearts(amount: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.HEARTS] = amount
            prefs[DataStoreKeys.CONSUMABLES_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun addHearts(amount: Int) {
        editUserData { prefs ->
            val current = prefs[DataStoreKeys.HEARTS] ?: 0
            prefs[DataStoreKeys.HEARTS] = current + amount
            prefs[DataStoreKeys.CONSUMABLES_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun setLastOpenDate(date: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.LAST_OPEN_DATE] = date
        }
    }

    override suspend fun setStrike(strike: Int) {
        editUserData { prefs ->
            prefs[DataStoreKeys.STRIKE] = strike
        }
        notifyStrikeWidgetDataChanged()
    }

    override suspend fun recordQuizCompletedDate(dateStr: String) {
        if (dateStr.isBlank()) return
        editUserData { prefs ->
            val currentRaw = prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] ?: ""
            val currentList = if (currentRaw.isBlank()) emptyList() else currentRaw.split(",").filter { it.isNotBlank() }
            val updatedList = (currentList + dateStr).distinct().sorted()
            val newStrike = com.shadowings.gero.util.calculateStreak(updatedList)
            val dateInt = com.shadowings.gero.util.dateStringToDateInt(dateStr)

            prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] = updatedList.joinToString(",")
            prefs[DataStoreKeys.STRIKE] = newStrike
            if (dateInt > 0) {
                prefs[DataStoreKeys.LAST_QUIZ_DATE] = dateInt
            }
        }
        notifyStrikeWidgetDataChanged()
    }

    override suspend fun setLastQuizDate(date: Int) {
        val dateStr = com.shadowings.gero.util.dateIntToString(date)
        if (dateStr.isNotBlank()) {
            recordQuizCompletedDate(dateStr)
        } else {
            editUserData { prefs ->
                prefs[DataStoreKeys.LAST_QUIZ_DATE] = date
            }
        }
    }

    override suspend fun setReminderEnabled(enabled: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.REMINDER_ENABLED] = enabled
        }
    }

    override suspend fun setReminderTime(hour: Int, minute: Int) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.REMINDER_HOUR] = hour.coerceIn(0, 23)
            prefs[DataStoreKeys.REMINDER_MINUTE] = minute.coerceIn(0, 59)
        }
    }

    override suspend fun setReviewPromptDone(done: Boolean) {
        editUserData { prefs ->
            prefs[DataStoreKeys.REVIEW_PROMPT_DONE] = done
        }
    }

    override suspend fun setFeaturePollAnswered(pollKey: String, answered: Boolean) {
        editUserData { prefs ->
            val current = decodeAnsweredFeaturePolls(prefs[DataStoreKeys.ANSWERED_FEATURE_POLLS])
            prefs[DataStoreKeys.ANSWERED_FEATURE_POLLS] = Json.encodeToString(current + (pollKey to answered))
        }
    }

    override suspend fun setCalendarPromptDone(done: Boolean) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.CALENDAR_PROMPT_DONE] = done
        }
    }

    override suspend fun setCalendarPromptDismissedAt(epochMillis: Long) {
        dataStore.edit { prefs ->
            prefs[DataStoreKeys.CALENDAR_PROMPT_DISMISSED_AT] = epochMillis
        }
    }

    override suspend fun resetAlynColors() {
        editUserData { prefs ->
            prefs[DataStoreKeys.alynColorKey("bg")] = 0
            prefs[DataStoreKeys.alynColorKey("skin")] = 0
            prefs[DataStoreKeys.alynColorKey("mouth")] = 0
            prefs[DataStoreKeys.alynColorKey("eyes")] = 1
            prefs[DataStoreKeys.alynColorKey("hair")] = 7
            prefs[DataStoreKeys.alynColorKey("earrings")] = 0
            prefs[DataStoreKeys.alynColorKey("shirt")] = 7
            prefs[DataStoreKeys.alynColorKey("hat")] = 0
            prefs[DataStoreKeys.alynColorKey("hatBar")] = 3
            prefs[DataStoreKeys.alynColorKey("hat_type")] = 0
        }
    }

    override suspend fun resetNiloColors() {
        editUserData { prefs ->
            prefs[DataStoreKeys.niloColorKey("bg")] = 0
            prefs[DataStoreKeys.niloColorKey("body")] = 7
            prefs[DataStoreKeys.niloColorKey("ear_left")] = 7
            prefs[DataStoreKeys.niloColorKey("ear_right")] = 7
            prefs[DataStoreKeys.niloColorKey("head")] = 0
            prefs[DataStoreKeys.niloColorKey("macchia_left")] = 7
            prefs[DataStoreKeys.niloColorKey("macchia_right")] = 7
            prefs[DataStoreKeys.niloColorKey("eyes")] = 7
            prefs[DataStoreKeys.niloColorKey("whiskers")] = 7
            prefs[DataStoreKeys.niloColorKey("macchie_type")] = 0
        }
    }

    override suspend fun resetZetaColors() {
        editUserData { prefs ->
            prefs[DataStoreKeys.zetaColorKey("bg")] = 0
            prefs[DataStoreKeys.zetaColorKey("skin")] = 0
            prefs[DataStoreKeys.zetaColorKey("hair")] = 9
            prefs[DataStoreKeys.zetaColorKey("dress")] = 5
            prefs[DataStoreKeys.zetaColorKey("glasses")] = 9
            prefs[DataStoreKeys.zetaColorKey("glasses_type")] = 0
            prefs[DataStoreKeys.zetaColorKey("glasses_accent")] = 7
        }
    }

    override suspend fun resetMaskoColors() {
        editUserData { prefs ->
            prefs[DataStoreKeys.maskoColorKey("bg")] = 9
            prefs[DataStoreKeys.maskoColorKey("skin")] = 0
            prefs[DataStoreKeys.maskoColorKey("glasses")] = 9
            prefs[DataStoreKeys.maskoColorKey("shirt")] = 7
            prefs[DataStoreKeys.maskoColorKey("baffi")] = 9
            prefs[DataStoreKeys.maskoColorKey("eyebrows")] = 9
            prefs[DataStoreKeys.maskoColorKey("eyes")] = 1
            prefs[DataStoreKeys.maskoColorKey("naso")] = 0
            prefs[DataStoreKeys.maskoColorKey("mask_type")] = 0
            prefs[DataStoreKeys.maskoColorKey("mask_accent")] = 9
        }
    }

    override suspend fun resetData() {
        dataStore.edit { prefs -> prefs.clear() }
        notifyStrikeWidgetDataChanged()
    }

    override suspend fun toggleFavoriteHiero(code: String) {
        val key = DataStoreKeys.favoriteHieroKey(code)
        editUserData { prefs ->
            if (prefs.contains(key)) {
                prefs.remove(key)
            } else {
                prefs[key] = 1
            }
            prefs[DataStoreKeys.FAVORITE_HIEROS_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun toggleFavoriteWord(id: String) {
        val key = DataStoreKeys.favoriteWordKey(id)
        editUserData { prefs ->
            if (prefs.contains(key)) {
                prefs.remove(key)
            } else {
                prefs[key] = 1
            }
            prefs[DataStoreKeys.FAVORITE_WORDS_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun restoreUserData(userData: UserData) {
        dataStore.edit { prefs ->
            val currentTestMode = prefs[DataStoreKeys.TEST_MODE] ?: false
            val currentShowCheats = prefs[DataStoreKeys.SHOW_CHEATS] ?: false
            val currentThemeMode = prefs[DataStoreKeys.THEME_MODE] ?: ThemeMode.System.storageValue
            val currentSoundEnabled = prefs[DataStoreKeys.SOUND_ENABLED] ?: true
            val currentHapticEnabled = prefs[DataStoreKeys.HAPTIC_ENABLED] ?: true
            val currentHideOptions = prefs[DataStoreKeys.HIDE_OPTIONS] ?: false
            val currentReminderEnabled = prefs[DataStoreKeys.REMINDER_ENABLED] ?: true
            val currentReminderHour = prefs[DataStoreKeys.REMINDER_HOUR] ?: 21
            val currentReminderMinute = prefs[DataStoreKeys.REMINDER_MINUTE] ?: 0
            val currentStarsBonus = prefs[DataStoreKeys.STARS_BONUS] ?: 0
            val currentCalendarPromptDone = prefs[DataStoreKeys.CALENDAR_PROMPT_DONE] ?: false
            val currentCalendarPromptDismissedAt = prefs[DataStoreKeys.CALENDAR_PROMPT_DISMISSED_AT] ?: 0L
            prefs.clear()
            prefs[DataStoreKeys.TEST_MODE] = currentTestMode
            prefs[DataStoreKeys.SHOW_CHEATS] = currentShowCheats
            prefs[DataStoreKeys.THEME_MODE] = currentThemeMode
            prefs[DataStoreKeys.SOUND_ENABLED] = currentSoundEnabled
            prefs[DataStoreKeys.HAPTIC_ENABLED] = currentHapticEnabled
            prefs[DataStoreKeys.HIDE_OPTIONS] = currentHideOptions
            prefs[DataStoreKeys.REMINDER_ENABLED] = currentReminderEnabled
            prefs[DataStoreKeys.REMINDER_HOUR] = currentReminderHour
            prefs[DataStoreKeys.REMINDER_MINUTE] = currentReminderMinute
            prefs[DataStoreKeys.CALENDAR_PROMPT_DONE] = currentCalendarPromptDone
            prefs[DataStoreKeys.CALENDAR_PROMPT_DISMISSED_AT] = currentCalendarPromptDismissedAt
            if (currentStarsBonus > 0) {
                prefs[DataStoreKeys.STARS_BONUS] = currentStarsBonus
            }

            userData.stars.forEach { (id, stars) ->
                prefs[DataStoreKeys.starsKey(id)] = stars
            }
            userData.perfect.forEach { id ->
                prefs[DataStoreKeys.perfectKey(id)] = 1
            }
            userData.explanations.forEach { id ->
                prefs[DataStoreKeys.explanationsKey(id)] = 1
            }
            prefs[DataStoreKeys.EXPERIENCE_TOTAL] = userData.experience
            prefs.remove(androidx.datastore.preferences.core.intPreferencesKey("coins_total"))
            prefs[DataStoreKeys.CONSUMABLES_UPDATED_AT] = userData.consumablesUpdatedAt.toLongOrNull() ?: 0L
            prefs[DataStoreKeys.LAST_MODIFIED] = userData.lastModified.toLongOrNull() ?: 0L
            val cleanDates = userData.completedQuizDates.filter { it.isNotBlank() }.distinct().sorted()
            val computedStrike = if (cleanDates.isNotEmpty()) com.shadowings.gero.util.calculateStreak(cleanDates) else userData.strike
            prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] = cleanDates.joinToString(",")
            prefs[DataStoreKeys.STRIKE] = computedStrike
            prefs[DataStoreKeys.LAST_QUIZ_DATE] = if (userData.lastQuizDate > 0) userData.lastQuizDate else {
                if (cleanDates.isNotEmpty()) com.shadowings.gero.util.dateStringToDateInt(cleanDates.last()) else 0
            }
            if (userData.tutorialDone == true) {
                prefs[DataStoreKeys.TUTORIAL_DONE] = 1
            }
            if (userData.walkthroughDone) {
                prefs[DataStoreKeys.WALKTHROUGH_DONE] = true
            }
            if (userData.compendioWalkthroughDone) {
                prefs[DataStoreKeys.COMPENDIO_WALKTHROUGH_DONE] = true
            }
            if (userData.builderWalkthroughDone) {
                prefs[DataStoreKeys.BUILDER_WALKTHROUGH_DONE] = true
            }
            if (userData.charactersWalkthroughDone) {
                prefs[DataStoreKeys.CHARACTERS_WALKTHROUGH_DONE] = true
            }
            if (userData.profileWalkthroughDone) {
                prefs[DataStoreKeys.PROFILE_WALKTHROUGH_DONE] = true
            }
            if (userData.lastSeenChangelogVersion.isNotBlank()) {
                prefs[DataStoreKeys.LAST_SEEN_CHANGELOG_VERSION] = userData.lastSeenChangelogVersion
            }
            userData.alynColors.forEach { (key, value) ->
                prefs[DataStoreKeys.alynColorKey(key)] = value
            }
            userData.niloColors.forEach { (key, value) ->
                prefs[DataStoreKeys.niloColorKey(key)] = value
            }
            userData.zetaColors.forEach { (key, value) ->
                prefs[DataStoreKeys.zetaColorKey(key)] = value
            }
            userData.maskoColors.forEach { (key, value) ->
                prefs[DataStoreKeys.maskoColorKey(key)] = value
            }
            prefs[DataStoreKeys.HEARTS] = userData.hearts
            prefs[DataStoreKeys.LAST_OPEN_DATE] = userData.lastOpenDate
            userData.favoriteHieros.forEach { code ->
                prefs[DataStoreKeys.favoriteHieroKey(code)] = 1
            }
            userData.favoriteWords.forEach { id ->
                prefs[DataStoreKeys.favoriteWordKey(id)] = 1
            }
            prefs[DataStoreKeys.APP_LANGUAGE] = userData.language
            if (userData.reviewPromptDone) {
                prefs[DataStoreKeys.REVIEW_PROMPT_DONE] = true
            }
            if (userData.savedBuilderWords.isNotEmpty()) {
                prefs[DataStoreKeys.SAVED_BUILDER_WORDS] = Json.encodeToString(userData.savedBuilderWords)
            }
            if (userData.isFullVersionUnlocked) {
                prefs[DataStoreKeys.IS_FULL_VERSION_UNLOCKED] = true
            }
            if (userData.streakRestorationTokens.isNotEmpty()) {
                prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS] = Json.encodeToString(userData.streakRestorationTokens)
            }
            if (userData.streakRestorationBackfillDone) {
                prefs[DataStoreKeys.STREAK_RESTORATION_BACKFILL_DONE] = true
            }
            if (userData.streakRestorationBadgeSeen) {
                prefs[DataStoreKeys.STREAK_RESTORATION_BADGE_SEEN] = true
            }
            if (userData.wrongAnswers.isNotEmpty()) {
                prefs[DataStoreKeys.WRONG_ANSWERS] = Json.encodeToString(userData.wrongAnswers)
            }
            if (userData.answeredFeaturePolls.isNotEmpty()) {
                prefs[DataStoreKeys.ANSWERED_FEATURE_POLLS] = Json.encodeToString(userData.answeredFeaturePolls)
            }
            prefs[DataStoreKeys.WRONG_ANSWERS_UPDATED_AT] = userData.wrongAnswersUpdatedAt.toLongOrNull() ?: 0L
            prefs[DataStoreKeys.FAVORITE_HIEROS_UPDATED_AT] = userData.favoriteHierosUpdatedAt.toLongOrNull() ?: 0L
            prefs[DataStoreKeys.FAVORITE_WORDS_UPDATED_AT] = userData.favoriteWordsUpdatedAt.toLongOrNull() ?: 0L
        }
        notifyStrikeWidgetDataChanged()
    }

    override suspend fun unlockAllLessons() {
        val allLessons = chapterOne + chapterTwo + chapterThree + chapterFour + chapterFive + chapterSix + chapterSeven + chapterEight + chapterNine + chapterTen + chapterEleven + chapterTwelve + chapterThirteen + chapterFourteen + chapterFifteen + chapterSixteen
        editUserData { prefs ->
            allLessons.forEach { lesson ->
                prefs[DataStoreKeys.starsKey(lesson.id)] = 3
                prefs[DataStoreKeys.perfectKey(lesson.id)] = 1
                prefs[DataStoreKeys.explanationsKey(lesson.id)] = 1
            }
        }
    }

    override suspend fun unlockLessonsUpToChapter9() {
        val lessonsThroughChapter8 =
            chapterOne + chapterTwo + chapterThree + chapterFour + chapterFive + chapterSix + chapterSeven + chapterEight
        editUserData { prefs ->
            lessonsThroughChapter8.forEach { lesson ->
                prefs[DataStoreKeys.starsKey(lesson.id)] = 3
                prefs[DataStoreKeys.perfectKey(lesson.id)] = 1
                prefs[DataStoreKeys.explanationsKey(lesson.id)] = 1
            }
        }
    }

    override suspend fun saveBuilderWord(word: SavedBuilderWord) {
        editUserData { prefs ->
            val currentList = runCatching {
                val raw = prefs[DataStoreKeys.SAVED_BUILDER_WORDS]
                if (!raw.isNullOrBlank()) {
                    Json.decodeFromString<List<SavedBuilderWord>>(raw)
                } else emptyList()
            }.getOrDefault(emptyList()).toMutableList()

            val index = currentList.indexOfFirst { it.id == word.id }
            if (index >= 0) {
                currentList[index] = word
            } else {
                currentList.add(0, word)
            }
            prefs[DataStoreKeys.SAVED_BUILDER_WORDS] = Json.encodeToString(currentList)
        }
    }

    override suspend fun deleteBuilderWord(id: String) {
        editUserData { prefs ->
            val currentList = runCatching {
                val raw = prefs[DataStoreKeys.SAVED_BUILDER_WORDS]
                if (!raw.isNullOrBlank()) {
                    Json.decodeFromString<List<SavedBuilderWord>>(raw)
                } else emptyList()
            }.getOrDefault(emptyList()).toMutableList()

            // Mark as a tombstone (with a fresh timestamp) instead of removing it outright, so
            // the cloud-sync merge can tell this delete apart from a stale copy that still has
            // the word. See CloudSyncRepositoryImpl.mergeSavedBuilderWordsByTimestamp.
            val index = currentList.indexOfFirst { it.id == id }
            if (index >= 0) {
                currentList[index] = currentList[index].copy(
                    deleted = true,
                    timestamp = currentTimeMillis().toString()
                )
                prefs[DataStoreKeys.SAVED_BUILDER_WORDS] = Json.encodeToString(currentList)
            }
        }
    }

    override suspend fun updateBuilderWord(word: SavedBuilderWord) {
        saveBuilderWord(word)
    }

    override suspend fun setFullVersionUnlocked(unlocked: Boolean) {
        editUserData { prefs ->
            prefs[DataStoreKeys.IS_FULL_VERSION_UNLOCKED] = unlocked
        }
    }

    override suspend fun ensureStreakRestorationTokensUpTo(level: Int) {
        if (level < 1) return
        editUserData { prefs ->
            val current = decodeStreakRestorationTokens(prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS])
            val updated = current.toMutableMap()
            for (l in 1..level) {
                val key = l.toString()
                if (!updated.containsKey(key)) {
                    updated[key] = false
                }
            }
            if (updated != current) {
                prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS] = Json.encodeToString(updated)
                // A newly granted token makes the badge relevant again, even if the user already
                // dismissed it for a previous, now-fully-redeemed batch.
                prefs[DataStoreKeys.STREAK_RESTORATION_BADGE_SEEN] = false
            }
        }
    }

    override suspend fun redeemStreakRestorationToken(dateStr: String) {
        if (dateStr.isBlank() || dateStr >= com.shadowings.gero.util.currentDateAsString()) return
        editUserData { prefs ->
            val tokens = decodeStreakRestorationTokens(prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS])
            val availableLevel = tokens.filterValues { !it }.keys.minByOrNull { it.toIntOrNull() ?: Int.MAX_VALUE }
            if (availableLevel == null) return@editUserData

            val currentRaw = prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] ?: ""
            val currentList = if (currentRaw.isBlank()) emptyList() else currentRaw.split(",").filter { it.isNotBlank() }
            if (currentList.contains(dateStr)) return@editUserData
            val updatedList = (currentList + dateStr).distinct().sorted()
            val newStrike = com.shadowings.gero.util.calculateStreak(updatedList)
            val dateInt = com.shadowings.gero.util.dateStringToDateInt(dateStr)

            prefs[DataStoreKeys.COMPLETED_QUIZ_DATES] = updatedList.joinToString(",")
            prefs[DataStoreKeys.STRIKE] = newStrike
            if (dateInt > 0) {
                // A restored day is always in the past: never move LAST_QUIZ_DATE backwards.
                prefs[DataStoreKeys.LAST_QUIZ_DATE] = maxOf(prefs[DataStoreKeys.LAST_QUIZ_DATE] ?: 0, dateInt)
            }
            prefs[DataStoreKeys.STREAK_RESTORATION_TOKENS] =
                Json.encodeToString(tokens + (availableLevel to true))
        }
        notifyStrikeWidgetDataChanged()
    }

    override suspend fun setStreakRestorationBackfillDone(done: Boolean) {
        editUserData { prefs ->
            prefs[DataStoreKeys.STREAK_RESTORATION_BACKFILL_DONE] = done
        }
    }

    override suspend fun setStreakRestorationBadgeSeen(seen: Boolean) {
        editUserData { prefs ->
            prefs[DataStoreKeys.STREAK_RESTORATION_BADGE_SEEN] = seen
        }
    }

    override suspend fun recordWrongAnswer(record: com.shadowings.gero.domain.model.WrongAnswerRecord) {
        editUserData { prefs ->
            val currentList = decodeWrongAnswers(prefs[DataStoreKeys.WRONG_ANSWERS]).toMutableList()
            val index = currentList.indexOfFirst { it.id == record.id }
            if (index >= 0) {
                currentList[index] = record.copy(attempts = currentList[index].attempts + 1)
            } else {
                currentList.add(0, record)
            }
            prefs[DataStoreKeys.WRONG_ANSWERS] = Json.encodeToString(currentList)
            prefs[DataStoreKeys.WRONG_ANSWERS_UPDATED_AT] = currentTimeMillis()
        }
    }

    override suspend fun removeWrongAnswer(id: String) {
        editUserData { prefs ->
            val currentList = decodeWrongAnswers(prefs[DataStoreKeys.WRONG_ANSWERS]).toMutableList()
            if (currentList.removeAll { it.id == id }) {
                prefs[DataStoreKeys.WRONG_ANSWERS] = Json.encodeToString(currentList)
                prefs[DataStoreKeys.WRONG_ANSWERS_UPDATED_AT] = currentTimeMillis()
            }
        }
    }

    // Bumps WRONG_ANSWERS_UPDATED_AT so the empty list wins the next cloud merge
    // (see CloudSyncRepositoryImpl) instead of the old cloud copy coming back.
    override suspend fun clearWrongAnswers() {
        editUserData { prefs ->
            prefs.remove(DataStoreKeys.WRONG_ANSWERS)
            prefs[DataStoreKeys.WRONG_ANSWERS_UPDATED_AT] = currentTimeMillis()
        }
    }
}

// Tolerates fields added by newer app versions: plain Json would fail on any unknown key and
// runCatching would then silently drop the whole saved mistakes list (e.g. after a downgrade, or
// an older device restoring records written by a newer one from the cloud).
private val wrongAnswersJson = Json { ignoreUnknownKeys = true }

internal fun decodeWrongAnswers(raw: String?): List<com.shadowings.gero.domain.model.WrongAnswerRecord> = runCatching {
    if (!raw.isNullOrBlank()) {
        wrongAnswersJson.decodeFromString<List<com.shadowings.gero.domain.model.WrongAnswerRecord>>(raw)
    } else {
        emptyList()
    }
}.getOrDefault(emptyList())
